import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { gamesService } from '../../services/games.service';
import { heroBannerService } from '../../services/hero-banner.service';
import { isCoAdminOrAdmin } from '../../utils/roles';
import './HeroBanner.css';

interface BannerAnnouncement {
  text: string;
  visible: boolean;
  updatedBy: string;
  updatedAt: string;
}

/**
 * Celebration background: add your file under `frontend/public/`.
 * - Video: `.mp4` (recommended) or `.webm` — loops, no sound (`muted` + `playsInline` for mobile).
 * - Animated GIF: use `.gif` (heavier than video).
 * - Static image: `.png` / `.jpg` works too.
 */
const HERO_CELEBRATION_SRC = '/ezgif-5d97791190fa5f40.mp4';
/** Optional still shown before video loads (e.g. existing art). */
const HERO_CELEBRATION_POSTER = '/hero-banner-celebration.png';

const HeroCelebrationMedia: React.FC = () => {
  const src = HERO_CELEBRATION_SRC;
  const lower = src.toLowerCase();
  const isVideo =
    lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.ogg');

  if (isVideo) {
    const mime = lower.endsWith('.webm')
      ? 'video/webm'
      : lower.endsWith('.ogg')
        ? 'video/ogg'
        : 'video/mp4';
    return (
      <video
        className="hero-celebration-media"
        autoPlay
        loop
        muted
        playsInline
        poster={HERO_CELEBRATION_POSTER}
        aria-hidden
      >
        <source src={src} type={mime} />
      </video>
    );
  }

  return <img src={src} alt="" className="hero-celebration-media" aria-hidden />;
};

const HeroBanner: React.FC = () => {
  const { user } = useAuth();
  // const navigate = useNavigate();
  const [bannerAnn, setBannerAnn] = useState<BannerAnnouncement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [avuruduTotal, setAvuruduTotal] = useState<number>(0);
  const [avuruduRank, setAvuruduRank] = useState<number | null>(null);

  const isAdmin = isCoAdminOrAdmin(user?.role);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await heroBannerService.get();
        if (!cancelled) {
          setBannerAnn({
            text: data.text || '',
            visible: data.visible,
            updatedBy: data.updatedBy || '',
            updatedAt: data.updatedAt,
          });
          try {
            localStorage.removeItem('hero_banner_announcement');
          } catch {
            /* ignore */
          }
        }
      } catch {
        if (!cancelled) setBannerAnn(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    const fetchAvuruduScore = async () => {
      try {
        const [myScores, leaderboard] = await Promise.all([
          gamesService.getMyScores(),
          gamesService.getLeaderboard(),
        ]);
        setAvuruduTotal(myScores.total ?? 0);
        const myEntry = leaderboard.find((e) => e.userId === user?.id);
        setAvuruduRank(myEntry?.rank ?? null);
      } catch {
        setAvuruduTotal(0);
        setAvuruduRank(null);
      }
    };
    if (user?.id) fetchAvuruduScore();
  }, [user?.id]);

  const handleSave = async () => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    try {
      const data = await heroBannerService.update({ text: trimmed, visible: true });
      setBannerAnn({
        text: data.text || '',
        visible: data.visible,
        updatedBy: data.updatedBy || '',
        updatedAt: data.updatedAt,
      });
      setIsEditing(false);
    } catch (err: any) {
      const m = err.response?.data?.message;
      alert(Array.isArray(m) ? m.join(' ') : m || err.message || 'Failed to save note');
    }
  };

  const handleToggleVisibility = async () => {
    if (!bannerAnn) return;
    try {
      const data = await heroBannerService.update({ visible: !bannerAnn.visible });
      setBannerAnn({
        text: data.text || '',
        visible: data.visible,
        updatedBy: data.updatedBy || '',
        updatedAt: data.updatedAt,
      });
    } catch (err: any) {
      const m = err.response?.data?.message;
      alert(Array.isArray(m) ? m.join(' ') : m || err.message || 'Failed to update visibility');
    }
  };

  const handleStartEdit = () => {
    setEditText(bannerAnn?.text || '');
    setIsEditing(true);
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const firstName = user?.fullName?.split(' ')[0] || 'there';
  const hasNote = !!(bannerAnn?.visible && bannerAnn.text);
  const showCard = isAdmin || hasNote;

  return (
    <div className="hero-banner">
      <div className='hero-celebration-bg' aria-hidden />
      <HeroCelebrationMedia />

      <div className="hero-left">
        <h2 className="hero-greeting">
          {getGreeting()}, {firstName} San ! 👋
        </h2>
        <p className="hero-subtitle">
          Here's what's happening at Toyota Lanka today:
        </p>

        {showCard && (
          <div className="hero-announcement-card">
            <div className="hero-ann-dept">
              HR Announcement
            </div>
            {hasNote && (
              <p className="hero-ann-subtext no-border">
                {bannerAnn!.text}
              </p>
            )}

            {isAdmin && (
              <div className="hero-ann-admin">
                {isEditing ? (
                  <div className="hero-ann-edit-row">
                    <input
                      className="hero-ann-input"
                      type="text"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      placeholder="Add a note..."
                      maxLength={120}
                      onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                      autoFocus
                    />
                    <button className="hero-ann-save-btn" onClick={handleSave}>Save</button>
                    <button className="hero-ann-cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
                  </div>
                ) : (
                  <div className="hero-ann-action-row">
                    <button className="hero-ann-edit-btn" onClick={handleStartEdit}>
                      {bannerAnn?.text ? 'Edit note' : '+ Add note'}
                    </button>
                    {bannerAnn?.text && (
                      <button className="hero-ann-toggle-btn" onClick={handleToggleVisibility}>
                        {bannerAnn.visible ? 'Hide' : 'Show'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="hero-points-section">
        <div className="hero-points-value">{avuruduTotal.toLocaleString()}</div>
        <div className="hero-points-label">Points</div>
        {avuruduRank !== null && (
          <div className="hero-points-rank">Rank #{avuruduRank}</div>
        )}
        
      </div>

      <div className="hero-mascot-wrapper">
        <img
          src="/hero-mascot.png"
          alt="Toyota Lanka Mascot"
          className="hero-mascot-img"
        />
      </div>
    </div>
  );
};

export default HeroBanner;
